<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue';
import { RouterView, useRouter } from 'vue-router';
import { registerSW } from 'virtual:pwa-register';
import { useAuthStore } from './stores/auth.js';
import { useSyncStore } from './stores/sync.js';

const auth = useAuthStore();
const sync = useSyncStore();
const router = useRouter();

const aktualisierungBereit = ref(false);
let aktualisierungAusfuehren: ((neuLaden?: boolean) => Promise<void>) | null = null;

onMounted(async () => {
  await auth.starten();
  await sync.starten();

  // Neue Version wird nie erzwungen – ein Neustart mitten in einer Aufnahme
  // wäre auf der Baustelle ärgerlich. Der Anwender entscheidet.
  aktualisierungAusfuehren = registerSW({
    immediate: true,
    onNeedRefresh() {
      aktualisierungBereit.value = true;
    },
  });
});

onUnmounted(() => sync.beenden());

async function aktualisieren(): Promise<void> {
  aktualisierungBereit.value = false;
  await aktualisierungAusfuehren?.(true);
}

async function erneutAnmelden(): Promise<void> {
  await router.push({ name: 'anmelden', query: { weiter: router.currentRoute.value.fullPath } });
}
</script>

<template>
  <div class="app-huelle">
    <!-- Sitzung abgelaufen: lokale Daten bleiben erhalten, es fehlt nur der Zugang. -->
    <div v-if="auth.status === 'sitzung-abgelaufen'" class="banner banner--warnung" role="status">
      <span>
        Die Sitzung ist abgelaufen. Deine Aufnahmen sind gespeichert und werden nach der Anmeldung
        übertragen.
      </span>
      <button type="button" class="knopf knopf--klein" @click="erneutAnmelden">Anmelden</button>
    </div>

    <div v-if="aktualisierungBereit" class="banner banner--info" role="status">
      <span>Eine neue Version ist verfügbar.</span>
      <button type="button" class="knopf knopf--klein" @click="aktualisieren">Jetzt laden</button>
    </div>

    <RouterView v-slot="{ Component }">
      <component :is="Component" />
    </RouterView>
  </div>
</template>

<style scoped>
.banner {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  padding: 0.625rem 1rem;
  padding-top: calc(0.625rem + env(safe-area-inset-top));
  font-size: 0.875rem;
  font-weight: 600;
}

.banner span {
  flex: 1;
  min-width: 12rem;
}

.banner--warnung {
  background: var(--farbe-warnung-hell);
  color: var(--farbe-warnung);
}

.banner--info {
  background: var(--farbe-marke-hell);
  color: var(--farbe-marke-dunkel);
}
</style>
